import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Search, Heart, LogIn, Smartphone, X, ChevronUp, Clock } from 'lucide-react';

const APP_STORE_URL  = 'https://apps.apple.com/us/app/yesod/id6748472269';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=us.yesod.app';

/**
 * Shared top navigation bar identical to the homepage header.
 * Props:
 *   cta?: { label: string, onClick: () => void }
 *     – overrides the default "Get the app" button on desktop
 *       and the footer CTA inside the drawer.
 */
export default function AppNav({ cta }) {
  const navigate   = useNavigate();
  const [navOpen, setNavOpen] = useState(false);

  const openApp = () => {
    const ua  = navigator.userAgent || '';
    const url = /android/i.test(ua) ? PLAY_STORE_URL : APP_STORE_URL;
    window.open(url, '_blank');
  };

  const ctaClick = cta ? cta.onClick : openApp;
  const ctaLabel = cta ? cta.label   : 'Get the app';

  return (
    <>
      {/* ── NAV ── */}
      <header className="hp-nav">
        <div className="hp-nav__inner">
          <a href="/" className="hp-nav__logo-wrap" aria-label="Yesod home">
            <img
              src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png"
              alt="Yesod"
              className="hp-nav__logo-img"
            />
          </a>

          {/* Desktop inline links */}
          <nav className="hp-nav__links">
            <button className="hp-nav__nav-link" onClick={() => navigate('/search')}>Communities</button>
            <button className="hp-nav__nav-link" onClick={() => navigate('/explore-prayers')}>Acts of Kindness</button>
            <button className="hp-nav__nav-link" onClick={() => navigate('/minyan')}>Find a Minyan</button>
            <button className="hp-nav__nav-link" onClick={() => navigate('/shabbat')}>Shabbat Times</button>
          </nav>

          <div className="hp-nav__actions">
            <button className="hp-nav__link hp-nav__link--desktop" onClick={() => navigate('/login')}>Sign in</button>
            <button className="hp-nav__cta hp-nav__cta--desktop" onClick={ctaClick}>{ctaLabel}</button>
            {/* Hamburger — mobile only */}
            <button
              className="hp-nav__burger"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={navOpen}
              onClick={() => setNavOpen(o => !o)}
            >
              <span className={`hp-nav__burger-icon${navOpen ? ' hp-nav__burger-icon--open' : ''}`}>
                <span /><span /><span />
              </span>
            </button>
          </div>
        </div>
      </header>

      {/* ── MOBILE DRAWER ── */}
      {navOpen && (
        <div className="hp-drawer-backdrop" onClick={() => setNavOpen(false)} aria-hidden="true" />
      )}
      <aside className={`hp-drawer${navOpen ? ' hp-drawer--open' : ''}`} aria-hidden={!navOpen}>
        <div className="hp-drawer__header">
          <img
            src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K%20(1).png"
            alt="Yesod"
            className="hp-drawer__logo"
          />
          <button className="hp-drawer__close" onClick={() => setNavOpen(false)} aria-label="Close menu">
            <X size={18} />
          </button>
        </div>
        <div className="hp-drawer__section-label">
          <span>Explore</span>
          <ChevronUp size={20} />
        </div>
        <div className="hp-drawer__top-divider" />
        <nav className="hp-drawer__nav">
          <button className="hp-drawer__item" onClick={() => { navigate('/search'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><Search size={20} /></span>
            Communities
          </button>
          <button className="hp-drawer__item" onClick={() => { navigate('/explore-prayers'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><Heart size={20} /></span>
            Chessed
          </button>
          <button className="hp-drawer__item" onClick={() => { navigate('/minyan'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><MapPin size={20} /></span>
            Find a Minyan
          </button>
          <button className="hp-drawer__item" onClick={() => { navigate('/shabbat'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><Clock size={20} /></span>
            Shabbat Times
          </button>
          <div className="hp-drawer__divider" />
          <button className="hp-drawer__item" onClick={() => { navigate('/login'); setNavOpen(false); }}>
            <span className="hp-drawer__item-icon"><LogIn size={20} /></span>
            Sign in
          </button>
        </nav>
        <div className="hp-drawer__footer">
          <button className="hp-drawer__cta" onClick={() => { ctaClick(); setNavOpen(false); }}>
            <Smartphone size={18} />
            Continue in App
          </button>
        </div>
      </aside>
    </>
  );
}
